import { Layout } from "@/components/layout";
import { useAuth } from "@/contexts/auth";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Users, Send, Loader2, Mail, Clock, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface ModRecord {
  id: number;
  fullName: string;
  email: string;
  createdAt: string;
}

interface Newsletter {
  id: number;
  subject: string;
  body: string;
  sentAt: string | null;
  sentByName: string | null;
  recipientCount: number;
  createdAt: string;
}

const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

async function fetchWithAuth(path: string, opts?: RequestInit) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export default function ModeratorTeam() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);

  if (!user || !["admin", "moderator"].includes(user.role)) {
    setLocation("/");
    return null;
  }

  const { data: mods = [], isLoading } = useQuery<ModRecord[]>({
    queryKey: ["moderator-team"],
    queryFn: () => fetchWithAuth("/api/admin/moderators"),
  });

  const { data: newsletters = [], isLoading: nlLoading } = useQuery<Newsletter[]>({
    queryKey: ["newsletters"],
    queryFn: () => fetchWithAuth("/api/newsletters"),
  });

  const send = useMutation({
    mutationFn: () => fetchWithAuth("/api/newsletters", {
      method: "POST",
      body: JSON.stringify({ subject: subject.trim(), body: body.trim() }),
    }),
    onSuccess: (data: { recipientCount?: number }) => {
      toast({ title: "Newsletter sent", description: `Delivered to ${data.recipientCount ?? 0} subscribers.` });
      setSubject("");
      setBody("");
      qc.invalidateQueries({ queryKey: ["newsletters"] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Mod Team</h1>
          <p className="text-muted-foreground font-mono text-sm mt-1">
            ACTIVE MODERATORS — {isLoading ? "..." : mods.length} ON DUTY
          </p>
        </div>

        {/* Count card */}
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="bg-blue-500/20 p-3 rounded-md">
              <ShieldCheck className="w-7 h-7 text-blue-400" />
            </div>
            <div>
              <div className="text-4xl font-bold font-mono text-blue-400">
                {isLoading ? "—" : mods.length}
              </div>
              <div className="text-sm text-muted-foreground font-mono tracking-wider mt-0.5">
                ACTIVE MODERATORS
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Roster */}
        {isLoading ? (
          <div className="text-blue-400 font-mono text-sm animate-pulse tracking-widest">LOADING ROSTER...</div>
        ) : mods.length === 0 ? (
          <Card className="border-border">
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground font-mono text-sm">No moderators assigned yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {mods.map((mod) => (
              <div
                key={mod.id}
                className={cn(
                  "border rounded-md bg-card/50 px-4 py-3 flex items-center gap-3",
                  mod.id === user?.id ? "border-blue-500/40" : "border-border"
                )}
              >
                <div className="bg-blue-500/15 p-1.5 rounded">
                  <ShieldCheck className="w-4 h-4 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{mod.fullName}</span>
                    {mod.id === user?.id && (
                      <span className="text-[10px] font-mono bg-blue-400/20 text-blue-300 border border-blue-400/30 rounded px-1.5 py-0.5">YOU</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground font-mono">{mod.email}</div>
                </div>
                <div className="text-xs text-muted-foreground/50 font-mono shrink-0">
                  Since {new Date(mod.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── NEWSLETTER COMPOSE ── */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Send Newsletter</h2>
          </div>
          <Card className="border-border bg-card">
            <CardContent className="pt-5 space-y-4">
              <p className="text-xs text-muted-foreground font-mono">
                Sends an in-app message to all users who have opted in to newsletters. Duplicate sends (same subject + body) are automatically rejected.
              </p>
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Subject</Label>
                <Input
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="e.g. New questions added to the CPL bank"
                  className="font-mono text-sm"
                  maxLength={200}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Body</Label>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="Write the newsletter content here..."
                  rows={8}
                  maxLength={8000}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                />
                <div className="text-[10px] text-muted-foreground/50 font-mono text-right">{body.length}/8000</div>
              </div>
              <Button
                disabled={!subject.trim() || !body.trim() || send.isPending}
                onClick={() => send.mutate()}
                className="font-mono text-xs uppercase tracking-widest"
              >
                {send.isPending ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Sending…</> : <><Send className="h-3.5 w-3.5 mr-2" />Send to Subscribers</>}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* ── NEWSLETTER HISTORY ── */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Newsletter History</h2>
          </div>
          {nlLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : newsletters.length === 0 ? (
            <Card className="border-border">
              <CardContent className="p-8 text-center">
                <Mail className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground font-mono text-sm">No newsletters sent yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {newsletters.map(nl => (
                <Card key={nl.id} className="border-border bg-card">
                  <CardContent className="p-0">
                    <button
                      className="w-full text-left px-4 py-3 flex items-start gap-3"
                      onClick={() => setExpanded(expanded === nl.id ? null : nl.id)}
                    >
                      <Mail className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm font-semibold text-foreground truncate">{nl.subject}</div>
                        <div className="text-xs text-muted-foreground font-mono flex flex-wrap gap-x-3 mt-0.5">
                          <span>{nl.sentAt ? new Date(nl.sentAt).toLocaleString() : "Draft"}</span>
                          <span>{nl.recipientCount} recipients</span>
                          {nl.sentByName && <span>by {nl.sentByName}</span>}
                        </div>
                      </div>
                      {expanded === nl.id ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
                    </button>
                    {expanded === nl.id && (
                      <div className="px-4 pb-4 pt-0 border-t border-border">
                        <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap leading-relaxed mt-3">{nl.body}</pre>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
